import type { PortalKey } from '@services/access';
import { PORTALS, portalPaths } from './portals';

export type ManagementPortal = 'admin' | 'org';
export type OrganizationTab = 'members' | 'teams' | 'usage' | 'settings';
export type EntitlementsTab = 'permissions' | 'minutes';

const id = encodeURIComponent;

// Builds a query string from the given pairs, skipping empty values.
function withQuery(path: string, query: [string, string | undefined][]): string {
  const params = new URLSearchParams();
  for (const [key, value] of query) if (value) params.set(key, value);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function organizationBase(portal: ManagementPortal, orgUuid: string): string {
  return portal === 'admin' ? `/admin/organizations/${id(orgUuid)}` : '/org/organization';
}

/** Every management link; the org admin's routes carry no organization id. */
export const managementPaths = {
  home: (portal: PortalKey): string => portalPaths(PORTALS[portal].basePath).dashboard,
  organizations: (): string => '/admin/organizations',
  organization: (portal: ManagementPortal, orgUuid: string, tab?: OrganizationTab): string =>
    withQuery(organizationBase(portal, orgUuid), [['tab', tab]]),
  team: (portal: ManagementPortal, orgUuid: string, teamUuid: string): string =>
    `${organizationBase(portal, orgUuid)}/teams/${id(teamUuid)}`,
  member: (portal: ManagementPortal, orgUuid: string, userUuid: string): string =>
    `${organizationBase(portal, orgUuid)}/members/${id(userUuid)}`,
  ownMember: (userUuid: string): string => `/org/organization/members/${id(userUuid)}`,
  entitlements: (
    portal: ManagementPortal,
    q: { org?: string; scope?: 'org' | 'user'; user?: string; tab?: EntitlementsTab } = {}
  ): string =>
    withQuery(`/${portal}/entitlements`, [
      ['org', q.org],
      ['scope', q.scope],
      ['user', q.user],
      ['tab', q.tab],
    ]),
  bookings: (portal: ManagementPortal, q: { org?: string } = {}): string =>
    withQuery(portalPaths(PORTALS[portal].basePath).bookings, [['org', q.org]]),
  users: (): string => '/admin/users',
  user: (userUuid: string): string => `/admin/users/${id(userUuid)}`,
  myTeam: (): string => '/user/team',
  myTeamMember: (userUuid: string): string => `/user/team/members/${id(userUuid)}`,
};
